import React, { useState, useRef, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Trash2, FileSignature } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Profile = () => {
  const { user } = useAuth();
  const [firma, setFirma] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchFirma();
    }
  }, [user]);

  const fetchFirma = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/${user.id}/firma`);
      if (response.data.firma) {
        setFirma(response.data.firma);
      }
    } catch (error) {
      console.error('Error al cargar la firma:', error);
    }
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveFirma = async () => {
    const canvas = canvasRef.current;
    const firmaBase64 = canvas.toDataURL('image/png');

    setLoading(true);
    try {
      await axios.post(`${API_URL}/users/${user.id}/firma`, { firma: firmaBase64 });
      setFirma(firmaBase64);
      toast.success('Firma guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar la firma');
    } finally {
      setLoading(false);
    }
  };

  const deleteFirma = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar tu firma?')) return;

    setLoading(true);
    try {
      await axios.delete(`${API_URL}/users/${user.id}/firma`);
      setFirma(null);
      clearCanvas();
      toast.success('Firma eliminada correctamente');
    } catch (error) {
      toast.error('Error al eliminar la firma');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Limpiar canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Dibujar imagen escalada
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;
        
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        
        // Guardar automáticamente
        await saveFirma();
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="profile-container">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-slate-900" data-testid="profile-title">
            Mi Perfil
          </h1>
          <p className="font-inter text-slate-600 mt-2">
            Gestiona tu información personal
          </p>
        </div>

        {/* Información del Usuario */}
        <Card>
          <CardHeader>
            <CardTitle>Información Personal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Nombre</p>
                <p className="font-medium text-slate-900">{user?.nombre}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Apellidos</p>
                <p className="font-medium text-slate-900">{user?.apellidos}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Email</p>
                <p className="font-medium text-slate-900">{user?.email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Rol</p>
                <p className="font-medium text-slate-900">{user?.role}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Firma Digital - Solo para Directores */}
        {user?.role === 'Director de formación' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSignature className="w-5 h-5" />
                Firma Digital
              </CardTitle>
              <CardDescription>
                Tu firma se incluirá en los certificados que generes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Firma Actual */}
              {firma && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Firma Actual:</p>
                  <div className="border border-slate-200 rounded-lg p-4 bg-white">
                    <img src={firma} alt="Firma actual" className="max-h-32 mx-auto" />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deleteFirma}
                    disabled={loading}
                    className="text-red-600 hover:text-red-700"
                    data-testid="delete-firma-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Firma
                  </Button>
                </div>
              )}

              {/* Canvas para Dibujar */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  {firma ? 'Actualizar Firma:' : 'Crear Firma:'}
                </p>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    className="bg-white border border-slate-200 rounded cursor-crosshair w-full"
                    style={{ touchAction: 'none' }}
                    data-testid="firma-canvas"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Dibuja tu firma con el ratón en el área blanca
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={clearCanvas}
                    data-testid="clear-canvas-button"
                  >
                    Limpiar
                  </Button>
                  <Button
                    onClick={saveFirma}
                    disabled={loading}
                    className="bg-[#004E89] hover:bg-[#003B66]"
                    data-testid="save-firma-button"
                  >
                    {loading ? 'Guardando...' : 'Guardar Firma'}
                  </Button>
                </div>
              </div>

              {/* Subir Imagen */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">O subir una imagen:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="firma-upload"
                    data-testid="firma-upload-input"
                  />
                  <label htmlFor="firma-upload">
                    <Button
                      as="span"
                      variant="outline"
                      disabled={loading}
                      data-testid="upload-firma-button"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Subir Imagen
                    </Button>
                  </label>
                  <p className="text-xs text-slate-500">PNG, JPG (máx. 2MB)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Profile;
